import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { statusClass, statusLabel } from '../utils/statusBadge';
import { toast } from 'sonner';
import {
  ShoppingCartIcon,
  PlusIcon,
  EyeIcon,
  TrashIcon,
  XMarkIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  SparklesIcon,
  ArrowUpTrayIcon,
  DocumentMagnifyingGlassIcon,
  ArchiveBoxArrowDownIcon,
  PencilIcon,
  ArrowUturnLeftIcon,
  ReceiptRefundIcon
} from '@heroicons/react/24/outline';
import { ChevronDownIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ButtonGroup } from '../components/ui/button-group';
import { Tip } from '../components/ui/tip';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Calendar } from '../components/ui/calendar';
import ErrorBoundary from '@/components/ErrorBoundary';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { useTheme } from '../contexts/ThemeContext';
import { cn } from '../lib/utils';
import { openPdf } from '../utils/pdfHelpers';
import { getAppSocket } from '../lib/wesSocket';
import { confirmDialog } from '../components/ConfirmDialog';

interface PurchaseItem {
  id?: string;
  productId?: string;
  supplyId?: string;
  itemType?: 'product' | 'supply' | 'manual';
  description: string;
  quantity: number;
  unitCost: number;
  subtotal?: number;
  taxRate?: number;
  taxAmount?: number;
  total: number;
  receivedQty?: number;
}

interface Purchase {
  id: string;
  purchaseNumber: string;
  providerId: string;
  providerName: string;
  providerDocument?: string;
  providerAddress?: string;
  issueDate: string;
  expectedDate?: string;
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  total: number;
  discount: number;
  items: PurchaseItem[];
  notes?: string;
  status: 'draft' | 'sent' | 'partial' | 'received' | 'cancelled';
  paymentMethod?: string | null;
  receivedTotal?: number;
  perceptions?: number;
  createdAt: string;
}

interface Provider {
  id: string;
  name: string;
  cuit?: string;
  email: string;
  phone: string;
  address?: string;
  type: 'individual' | 'company';
  paymentMethod?: string | null;
}

interface Product {
  id: string;
  name: string;
  sku: string;
  costPrice: number;
  sellingPrice: number;
  currentStock: number;
  category?: string;
  isService?: boolean;
  description?: string;
}

interface Supply {
  id: string;
  name: string;
  category?: string;
  costPrice?: number;
  stock?: number;
}

// DatePicker component
const DatePickerCalendar = ({ value, onChange, id }: { value?: string; onChange: (date: string) => void; id?: string }) => {
  const [open, setOpen] = useState(false);

  // Parsear la fecha correctamente para evitar problemas de zona horaria
  const dateValue = value ? (() => {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  })() : undefined;

  const handleSelect = (date: Date | undefined) => {
    if (date) {
      // Formatear la fecha correctamente en zona horaria local
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      onChange(`${year}-${month}-${day}`);
      setOpen(false);
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          id={id}
          className={cn(
            "w-full justify-between font-normal px-4 py-3 rounded-xl border border-input bg-background h-auto",
            !dateValue && "text-muted-foreground"
          )}
        >
          {dateValue ? dateValue.toLocaleDateString('es-AR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          }) : 'Seleccionar fecha'}
          <ChevronDownIcon className="ml-2 h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto overflow-hidden p-0" align="start">
        <Calendar
          mode="single"
          selected={dateValue}
          onSelect={handleSelect}
          captionLayout="dropdown"
          fromYear={currentYear - 1}
          toYear={currentYear + 1}
          disabled={(date) => date > new Date() || date < new Date('1900-01-01')}
        />
      </PopoverContent>
    </Popover>
  );
};

const Purchases: React.FC = () => {
  const { isDarkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPurchase, setSelectedPurchase] = useState<Purchase | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [receiveItems, setReceiveItems] = useState<Record<string, number>>({});
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    providerId: '',
    from: '',
    to: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showScanModal, setShowScanModal] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState<any>(null);
  const scanFileRef = React.useRef<HTMLInputElement>(null);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(amount || 0);

  // La recepción se mide contra la mercadería, no contra el total de la factura:
  // las percepciones (IIBB) suman al total pero no entran al depósito, así que
  // compararlas contra receivedTotal dejaba la compra como parcial para siempre.
  const recepcionCompleta = (purchase: Purchase) => {
    if (purchase.status === 'received') return true;
    const recibible = Number(purchase.total || 0) - Number(purchase.perceptions || 0);
    return Number(purchase.receivedTotal || 0) >= recibible;
  };

  // Fechas date-only ('2026-08-03') no pasan por Date: el navegador las lee
  // como medianoche UTC y las corre un día para atrás en Argentina.
  const formatDay = (value: string) => {
    const s = String(value || '');
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (m) return `${Number(m[3])}/${Number(m[2])}/${m[1]}`;
    const d = new Date(s);
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
  };

  // Fetch data
  useEffect(() => {
    fetchProviders();
  }, []);

  useEffect(() => {
    const query = new URLSearchParams(location.search).get('q');
    if (query && query !== filters.search) {
      setFilters((prev) => ({ ...prev, search: query }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Guard de secuencia: si dos búsquedas quedan en vuelo (ej. al llegar con ?q= en
  // la URL, el fetch sin filtro y el filtrado corren juntos), solo aplica la última.
  const fetchSeqRef = useRef(0);
  const fetchPurchases = async () => {
    const seq = ++fetchSeqRef.current;
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      if (filters.providerId) params.append('providerId', filters.providerId);
      if (filters.from) params.append('from', filters.from);
      if (filters.to) params.append('to', filters.to);

      const response = await axios.get(`/api/purchases?${params}`);
      if (seq !== fetchSeqRef.current) return; // llegó una respuesta más nueva
      setPurchases(response.data.purchases || []);
    } catch (error) {
      if (seq === fetchSeqRef.current) toast.error('Error al cargar las ordenes de compra');
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  };

  const fetchProviders = async () => {
    try {
      const response = await axios.get('/api/providers?limit=1000');
      setProviders(response.data.providers || []);
    } catch (error) {
      console.error('Error fetching providers:', error);
    }
  };

  // AI Invoice Scanner
  const handleScanInvoice = async (file: File) => {
    setScanning(true);
    setScanResult(null);
    try {
      const formData = new FormData();
      formData.append('invoice', file);
      const response = await axios.post('/api/purchases/scan-invoice', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      if (response.data?.success && response.data?.data) {
        setScanResult(response.data.data);
        toast.success('Factura analizada correctamente');
      } else {
        toast.error('No se pudo analizar la factura');
      }
    } catch (error: any) {
      console.error('Error scanning invoice:', error);
      toast.error(error.response?.data?.error || 'Error al escanear la factura');
    } finally {
      setScanning(false);
    }
  };

  // Llevar el resultado del escaneo IA al formulario (página /purchases/new)
  const applyScanResult = () => {
    if (!scanResult) return;
    const scan = scanResult;
    setShowScanModal(false);
    setScanResult(null);
    toast.success('Datos cargados en el formulario');
    navigate('/purchases/new', { state: { scan } });
  };

  // Realtime: refrescar en vivo por sucursal (sin polling)
  useEffect(() => {
    const socket = getAppSocket();
    let refreshTimer: number | null = null;

    const scheduleSilentRefresh = (payload?: any) => {
      const path = String(payload?.path || '');
      if (
        path &&
        !(
          path.startsWith('/api/purchases') ||
          path.startsWith('/api/providers') ||
          path.startsWith('/api/products') ||
          path.startsWith('/api/supplies')
        )
      ) {
        return;
      }

      if (refreshTimer) window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => {
        fetchPurchases();
        fetchProviders();
      }, 450);
    };

    socket.on('area:update', scheduleSilentRefresh);
    socket.on('connect', scheduleSilentRefresh);

    return () => {
      if (refreshTimer) window.clearTimeout(refreshTimer);
      socket.off('area:update', scheduleSilentRefresh);
      socket.off('connect', scheduleSilentRefresh);
    };
  }, [filters.search, filters.status, filters.providerId, filters.from, filters.to]);

  // Resetear página cuando cambian los filtros
  useEffect(() => {
    setCurrentPage(1);
    fetchPurchases();
  }, [filters.search, filters.status, filters.providerId, filters.from, filters.to]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Solo activar si no estamos escribiendo en un input
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        event.target instanceof HTMLSelectElement
      ) {
        return;
      }

      const key = event.key.toUpperCase();

      // N = Nueva Compra (página propia)
      if (key === 'N') {
        event.preventDefault();
        navigate('/purchases/new');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  // Cálculos de paginación
  const totalPages = Math.ceil(purchases.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedPurchases = purchases.slice(startIndex, endIndex);
  const totalResults = purchases.length;
  const startResult = purchases.length === 0 ? 0 : startIndex + 1;
  const endResult = Math.min(endIndex, purchases.length);

  // Funciones de navegación
  const goToFirstPage = () => setCurrentPage(1);
  const goToPreviousPage = () => setCurrentPage(prev => Math.max(1, prev - 1));
  const goToNextPage = () => setCurrentPage(prev => Math.min(totalPages, prev + 1));
  const goToLastPage = () => setCurrentPage(totalPages);

  const handleDelete = async (purchase: Purchase) => {
    const warning = purchase.status === 'received' || purchase.status === 'partial'
      ? 'Esta compra ya sumó stock y deuda. Al eliminarla se revierten automáticamente (el stock y la cta cte quedan como si la orden nunca hubiera existido).\n\n¿Eliminarla?'
      : '¿Seguro que querés eliminar esta compra?';
    if (!await confirmDialog(warning)) return;

    try {
      await axios.delete(`/api/purchases/${purchase.id}`);
      toast.success('Orden de compra eliminada');
      fetchPurchases();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al eliminar');
    }
  };

  // Reabrir = des-recepcionar: revierte stock + deuda y la deja pendiente/editable,
  // conservando la orden. Para corregir o cuando se marcó recibida antes de tiempo.
  const handleReopen = async (purchase: Purchase) => {
    const ok = await confirmDialog('Reabrir esta compra la deja PENDIENTE de recepción: se revierten el stock y la deuda que había sumado, y queda editable. Después la recibís de nuevo cuando corresponda.\n\n¿Reabrir?');
    if (!ok) return;
    try {
      await axios.post(`/api/purchases/${purchase.id}/reopen`);
      toast.success('Compra reabierta (pendiente de recepción)');
      fetchPurchases();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al reabrir');
    }
  };

  // Marcar recibida de un click: recepciona todo lo pendiente (impacta stock y costos).
  // Para recepción parcial está el detalle (ojo) con cantidades por ítem.
  const handleReceiveAll = async (purchase: Purchase) => {
    const itemsPayload = (purchase.items || [])
      .filter((item) => item.id)
      .map((item) => ({ itemId: item.id as string, qty: Math.max(0, (item.quantity || 0) - (item.receivedQty || 0)) }))
      .filter((entry) => entry.qty > 0);

    if (itemsPayload.length === 0) {
      toast.error('No hay cantidades pendientes de recibir');
      return;
    }
    if (!await confirmDialog(`¿Marcar recibida la compra ${purchase.purchaseNumber}? Se actualiza el stock y el costo de los productos.`)) return;

    try {
      await axios.post(`/api/purchases/${purchase.id}/receive`, { items: itemsPayload });
      toast.success('Compra recibida — stock actualizado');
      fetchPurchases();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'No se pudo recepcionar la compra');
    }
  };

  const getStatusColor = (status: string) => statusClass('purchase', status);
  const getStatusLabel = (status: string) => statusLabel('purchase', status);

  const handlePrintPDF = async (purchase: Purchase) => {
    try {
      const filename = purchase.purchaseNumber
        ? `Compra-${purchase.purchaseNumber}.pdf`
        : `Compra-${purchase.id.slice(-6)}.pdf`;

      const response = await axios.get(`/api/purchases/${purchase.id}/pdf`, {
        responseType: 'blob'
      });

      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const result = openPdf({ blob: pdfBlob, filename });

      if (result === 'downloaded') {
        toast.warning('Permití las ventanas emergentes para ver el PDF en una pestaña nueva. Se descargó automáticamente.');
      }
    } catch (error) {
      console.error('Error abriendo PDF:', error);
      toast.error('Error al generar el PDF');
    }
  };

  const initReceiveItems = (purchase: Purchase) => {
    const map: Record<string, number> = {};
    purchase.items.forEach((item) => {
      if (item.id) {
        map[item.id] = 0;
      }
    });
    setReceiveItems(map);
  };

  const handleReceive = async () => {
    if (!selectedPurchase) return;
    const itemsPayload = Object.entries(receiveItems)
      .map(([itemId, qty]) => ({ itemId, qty: Number(qty || 0) }))
      .filter((entry) => entry.qty > 0);

    if (itemsPayload.length === 0) {
      toast.error('Ingresa una cantidad para recepcionar');
      return;
    }

    try {
      const response = await axios.post(`/api/purchases/${selectedPurchase.id}/receive`, {
        items: itemsPayload
      });
      toast.success('Recepción registrada');
      setSelectedPurchase(response.data.purchase);
      initReceiveItems(response.data.purchase);
      fetchPurchases();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al registrar la recepción');
    }
  };

  return (
    <ErrorBoundary>
      <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <h2 className="text-2xl font-bold tracking-tight">Compras</h2>
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
          <Button
            onClick={() => navigate('/purchases/new')}
            className="inline-flex items-center justify-center whitespace-nowrap text-sm font-bold h-8 rounded-lg gap-1.5 px-3 bg-black dark:bg-white text-white dark:text-black shadow-xs hover:bg-black/90 dark:hover:bg-white/90"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Nueva orden
            <kbd className="ml-2 inline-flex items-center justify-center px-1 text-xs font-bold font-sans bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 rounded-sm border leading-none">
              N
            </kbd>
          </Button>
          <Button
            onClick={() => setShowScanModal(true)}
            variant="outline"
            className="h-8 rounded-lg gap-1.5 px-3 border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20"
          >
            <SparklesIcon className="h-4 w-4" />
            Escanear factura IA
          </Button>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:flex-wrap">
        <div className="relative w-full md:w-[260px]">
          <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none z-10" />
          <Input
            type="text"
            placeholder="Buscar por número, proveedor..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex min-w-0 rounded-lg border bg-white px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive h-8 w-full pl-8"
          />
        </div>
        <div className="w-full md:w-[220px]">
          <Label className="text-xs text-muted-foreground">Estado</Label>
          <Select
            value={filters.status || 'all'}
            onValueChange={(value) => setFilters({ ...filters, status: value === 'all' ? '' : value })}
          >
            <SelectTrigger className="h-9 rounded-lg">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="pending_receive">Pendientes de recibir</SelectItem>
              <SelectItem value="draft">Borrador</SelectItem>
              <SelectItem value="sent">Enviada</SelectItem>
              <SelectItem value="partial">Recibida parcial</SelectItem>
              <SelectItem value="received">Recibida</SelectItem>
              <SelectItem value="cancelled">Cancelada</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-full md:w-[260px]">
          <Label className="text-xs text-muted-foreground">Proveedor</Label>
          <Select
            value={filters.providerId || 'all'}
            onValueChange={(value) => setFilters({ ...filters, providerId: value === 'all' ? '' : value })}
          >
            <SelectTrigger className="h-9 rounded-lg">
              <SelectValue placeholder="Todos los proveedores" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los proveedores</SelectItem>
              {providers.map((provider) => (
                <SelectItem key={provider.id} value={provider.id}>
                  {(provider as any).fantasyName ? `${(provider as any).fantasyName} — ${provider.name}` : provider.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-full md:w-[180px]">
          <Label className="text-xs text-muted-foreground">Desde</Label>
          <DatePickerCalendar
            value={filters.from}
            onChange={(date) => setFilters({ ...filters, from: date })}
          />
        </div>
        <div className="w-full md:w-[180px]">
          <Label className="text-xs text-muted-foreground">Hasta</Label>
          <DatePickerCalendar
            value={filters.to}
            onChange={(date) => setFilters({ ...filters, to: date })}
          />
        </div>
      </div>

      {/* Purchases list */}
      <Card className="border-0 shadow-none md:border md:shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex flex-col items-center justify-center gap-2 py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-foreground"></div>
              <p className="text-sm text-muted-foreground">Cargando compras...</p>
            </div>
          ) : purchases.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCartIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No hay órdenes de compra</p>
            </div>
          ) : (
            <>
            <div className="hidden md:block relative w-full cq-table">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-muted/50 border-b">
                    <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">N° Orden</TableHead>
                    <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Proveedor</TableHead>
                    <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Fecha</TableHead>
                    <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left cq-hide-narrow">Pago</TableHead>
                    <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-right">Total</TableHead>
                    <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-right cq-hide-narrow">Recibido</TableHead>
                    <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Estado</TableHead>
                    <TableHead className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedPurchases.map((purchase) => (
                    <TableRow key={purchase.id} className="border-b hover:bg-muted/50 cursor-pointer">
                      <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                        <div className="font-medium">{purchase.purchaseNumber}</div>
                      </TableCell>
                      <TableCell className="p-2 align-middle text-left">
                        <div className="max-w-[260px] truncate cq-clamp-narrow">{purchase.providerName}</div>
                        {(purchase as any).appointmentCode && (
                          <div className="text-xs text-cyan-600 dark:text-cyan-400 font-mono">
                            {(purchase as any).appointmentCode}
                            {(purchase as any).appointmentPlate ? ` · ${(purchase as any).appointmentPlate}` : ''}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                        <div>{formatDay(purchase.issueDate)}</div>
                      </TableCell>
                      <TableCell className="p-2 align-middle whitespace-nowrap text-left cq-hide-narrow">
                        <div className="capitalize">{purchase.paymentMethod?.replace('_', ' ') || '—'}</div>
                      </TableCell>
                      <TableCell className="p-2 align-middle whitespace-nowrap text-right">
                        <div className="font-medium">{formatCurrency(purchase.total)}</div>
                      </TableCell>
                      <TableCell className="p-2 align-middle whitespace-nowrap text-right cq-hide-narrow">
                        <div className="text-sm">
                          {/* Completa: sin repetir el total. Parcial: cuánto entró de cuánto. */}
                          {!purchase.receivedTotal
                            ? '—'
                            : recepcionCompleta(purchase)
                              ? <span className="text-emerald-600 dark:text-emerald-400 font-medium">Completa</span>
                              : `${formatCurrency(purchase.receivedTotal)} / ${formatCurrency(purchase.total)}`}
                        </div>
                      </TableCell>
                      <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                        <Badge
                          variant="secondary"
                          className={getStatusColor(purchase.status)}
                        >
                          {getStatusLabel(purchase.status)}
                        </Badge>
                      </TableCell>
                      <TableCell className="p-2 align-middle whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end">
                          <ButtonGroup>
                            <Tip label="Ver detalles">
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/purchases/${purchase.id}/view`);
                                }}
                                className="size-8"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </Button>
                            </Tip>
                            {(purchase.status === 'draft' || purchase.status === 'sent') && (
                              <Tip label="Editar orden">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/purchases/${purchase.id}/edit`);
                                  }}
                                  className="size-8"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </Button>
                              </Tip>
                            )}
                            {(purchase.status === 'draft' || purchase.status === 'sent' || purchase.status === 'partial') && (
                              <Tip label="Marcar recibida (impacta stock)">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleReceiveAll(purchase);
                                  }}
                                  className="size-8"
                                >
                                  <ArchiveBoxArrowDownIcon className="h-4 w-4" />
                                </Button>
                              </Tip>
                            )}
                            {(purchase.status === 'received' || purchase.status === 'partial') && (
                              <Tip label="Nota de crédito (devolución / ajuste del proveedor)">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/purchases/credit-notes/new?purchaseId=${purchase.id}`);
                                  }}
                                  className="size-8"
                                >
                                  <ReceiptRefundIcon className="h-4 w-4" />
                                </Button>
                              </Tip>
                            )}
                            {(purchase.status === 'received' || purchase.status === 'partial') && (
                              <Tip label="Reabrir (revierte stock y deuda; la deja pendiente y editable)">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleReopen(purchase);
                                  }}
                                  className="size-8"
                                >
                                  <ArrowUturnLeftIcon className="h-4 w-4" />
                                </Button>
                              </Tip>
                            )}
                            <Tip label="Imprimir PDF">
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePrintPDF(purchase);
                                }}
                                className="size-8"
                              >
                                <DocumentTextIcon className="h-4 w-4" />
                              </Button>
                            </Tip>
                            {purchase.status !== 'cancelled' && (
                              <Tip label="Eliminar compra (revierte stock y deuda automáticamente)">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(purchase);
                                  }}
                                  className="size-8"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </Button>
                              </Tip>
                            )}
                          </ButtonGroup>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="md:hidden space-y-3 p-4">
              {paginatedPurchases.map((purchase) => (
                <div key={purchase.id} className="rounded-xl border bg-card/80 p-4 shadow-sm space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs text-muted-foreground">N° Orden</p>
                      <p className="text-base font-semibold text-foreground">{purchase.purchaseNumber}</p>
                      <p className="text-sm text-muted-foreground">{purchase.providerName}</p>
                    </div>
                    <Badge variant="secondary" className={getStatusColor(purchase.status)}>
                      {getStatusLabel(purchase.status)}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs uppercase text-muted-foreground">Fecha</p>
                      <p className="font-medium text-foreground">{formatDay(purchase.issueDate)}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-muted-foreground">Total</p>
                      <p className="font-semibold text-primary">{formatCurrency(purchase.total)}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-muted-foreground">Pago</p>
                      <p className="font-medium text-foreground capitalize">{purchase.paymentMethod?.replace('_', ' ') || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-muted-foreground">Recibido</p>
                      <p className="font-medium text-foreground">
                        {!purchase.receivedTotal
                          ? '—'
                          : recepcionCompleta(purchase)
                            ? <span className="text-emerald-600 dark:text-emerald-400">Completa</span>
                            : `${formatCurrency(purchase.receivedTotal)} / ${formatCurrency(purchase.total)}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 min-w-[120px] justify-center"
                      onClick={() => navigate(`/purchases/${purchase.id}/view`)}
                    >
                      <EyeIcon className="h-4 w-4 mr-1" />
                      Ver
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 min-w-[120px] justify-center"
                      onClick={() => handlePrintPDF(purchase)}
                    >
                      <DocumentTextIcon className="h-4 w-4 mr-1" />
                      PDF
                    </Button>
                    {purchase.status !== 'cancelled' && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 min-w-[120px] justify-center text-destructive"
                        onClick={() => handleDelete(purchase)}
                      >
                        <TrashIcon className="h-4 w-4 mr-1" />
                        Eliminar
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Paginación */}
      {!loading && purchases.length > 0 && (
        <div className="flex flex-col gap-3 px-2 md:flex-row md:items-center md:justify-between">
          <div className="flex-1 text-center text-muted-foreground text-sm md:text-left">
            Mostrando {startResult} de {totalResults} resultados
          </div>
          <div className="flex items-center justify-center space-x-6 lg:space-x-8">
            <div className="flex w-[100px] items-center justify-center font-medium text-sm">
              Página {currentPage} de {totalPages}
            </div>
          </div>
          <div className="flex items-center justify-center space-x-2 md:justify-end">
            <Button
              variant="outline"
              size="icon"
              onClick={goToFirstPage}
              disabled={currentPage === 1}
              className="shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 hidden h-8 w-8 lg:flex"
            >
              <span className="sr-only">Ir a la primera página</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                <path d="m11 17-5-5 5-5"></path>
                <path d="m18 17-5-5 5-5"></path>
              </svg>
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 h-8 w-8"
            >
              <span className="sr-only">Ir a la página anterior</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                <path d="m15 18-6-6 6-6"></path>
              </svg>
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 h-8 w-8"
            >
              <span className="sr-only">Ir a la página siguiente</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                <path d="m9 18 6-6-6-6"></path>
              </svg>
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={goToLastPage}
              disabled={currentPage === totalPages}
              className="shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 hidden h-8 w-8 lg:flex"
            >
              <span className="sr-only">Ir a la última página</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                <path d="m6 17 5-5-5-5"></path>
                <path d="m13 17 5-5-5-5"></path>
              </svg>
            </Button>
          </div>
        </div>
      )}


      {/* View Modal */}

      {/* AI Invoice Scanner Modal */}
      <Dialog open={showScanModal} onOpenChange={setShowScanModal}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <SparklesIcon className="h-5 w-5 text-purple-600" />
              Escanear Factura con IA
            </DialogTitle>
          </DialogHeader>

          {!scanResult ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Subi una foto o PDF de la factura de compra del proveedor. La IA analizara el documento y precargara los datos en el formulario de orden de compra.
              </p>

              <div
                onClick={() => !scanning && scanFileRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                  scanning
                    ? 'border-purple-400 bg-purple-50 dark:bg-purple-900/20'
                    : 'border-muted-foreground/25 hover:border-purple-400 hover:bg-purple-50/50 dark:hover:bg-purple-900/10'
                }`}
              >
                {scanning ? (
                  <div className="flex flex-col items-center gap-3">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div>
                    <p className="text-sm font-medium text-purple-700 dark:text-purple-300">Analizando factura con IA...</p>
                    <p className="text-xs text-muted-foreground">Esto puede tardar unos segundos</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <ArrowUpTrayIcon className="h-10 w-10 text-muted-foreground/50" />
                    <div>
                      <p className="text-sm font-medium">Click para seleccionar archivo</p>
                      <p className="text-xs text-muted-foreground mt-1">PDF, JPG o PNG - Max 15MB</p>
                    </div>
                  </div>
                )}
              </div>

              <input
                ref={scanFileRef}
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleScanInvoice(file);
                  e.target.value = '';
                }}
              />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                <DocumentMagnifyingGlassIcon className="h-5 w-5" />
                <span className="text-sm font-medium">Factura analizada correctamente</span>
                {scanResult.confidence && (
                  <Badge variant="secondary" className="ml-auto text-xs">
                    Confianza: {Math.round(scanResult.confidence * 100)}%
                  </Badge>
                )}
              </div>

              <div className="bg-muted/50 rounded-lg p-4 space-y-3 text-sm max-h-[350px] overflow-y-auto">
                {scanResult.provider && (
                  <div>
                    <span className="text-xs font-medium text-muted-foreground uppercase">Proveedor</span>
                    <p className="font-medium">{scanResult.provider.name || '-'}</p>
                    {scanResult.provider.cuit && <p className="text-xs text-muted-foreground">CUIT: {scanResult.provider.cuit}</p>}
                    {scanResult.provider.matchedName && (
                      <Badge variant="secondary" className="mt-1 text-xs bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                        Coincide con: {scanResult.provider.matchedName}
                      </Badge>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="text-xs font-medium text-muted-foreground uppercase">N° Factura</span>
                    <p className="font-medium">{scanResult.invoiceNumber || '-'}</p>
                  </div>
                  <div>
                    <span className="text-xs font-medium text-muted-foreground uppercase">Fecha</span>
                    <p className="font-medium">{scanResult.date || '-'}</p>
                  </div>
                </div>

                {scanResult.items && scanResult.items.length > 0 && (
                  <div>
                    <span className="text-xs font-medium text-muted-foreground uppercase">Items ({scanResult.items.length})</span>
                    <div className="mt-1 space-y-1">
                      {scanResult.items.map((item: any, idx: number) => (
                        <div key={idx} className="flex justify-between items-center py-1 border-b border-border/50 last:border-0">
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium truncate">{item.name}</p>
                            {(item.matchedProductName || item.matchedSupplyName) && (
                              <p className="text-xs text-green-600">= {item.matchedProductName || item.matchedSupplyName}</p>
                            )}
                          </div>
                          <div className="text-right text-xs ml-2 shrink-0">
                            <span>{item.qty} x {formatCurrency(item.unitCost)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-3 gap-2 pt-2 border-t">
                  <div>
                    <span className="text-xs font-medium text-muted-foreground uppercase">Subtotal</span>
                    <p className="font-medium">{formatCurrency(scanResult.subtotal || 0)}</p>
                  </div>
                  <div>
                    <span className="text-xs font-medium text-muted-foreground uppercase">IVA</span>
                    <p className="font-medium">{formatCurrency(scanResult.tax || 0)}</p>
                  </div>
                  <div>
                    <span className="text-xs font-medium text-muted-foreground uppercase">Total</span>
                    <p className="font-bold">{formatCurrency(scanResult.total || 0)}</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setScanResult(null);
                  }}
                  className="h-9 rounded-lg"
                >
                  Escanear otra
                </Button>
                <Button
                  onClick={applyScanResult}
                  className="h-9 rounded-lg gap-1.5 bg-purple-600 hover:bg-purple-700 text-white"
                >
                  <SparklesIcon className="h-4 w-4" />
                  Cargar en formulario
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      </div>
    </ErrorBoundary>
  );
};

export default Purchases;






